import React from "react";
import { Paper, Grid, Fab } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { HashLink } from "react-router-hash-link";

import ApiAttribute from "components/ApiAttribute/ApiAttribute";
import CodeCard from "components/shared/CodeCard.js";

import {
  QUERY_PARAMS,
  RESULTS,
  ACCOUNT_JSON,
  ACCOUNT,
  BIN,
  BIN_JSON,
  CHANNEL,
  CHANNEL_JSON,
  CLASS,
  CLASS_JSON,
  CUSTOM_FIELD,
  CUSTOM_FIELD_JSON,
  DEPARTMENT,
  LOCATION,
  LOCATION_JSON,
  ORDER_STAGE,
  PAYMENT_METHOD,
  PAYMENT_METHOD_JSON,
  PRICE_TIER,
  PRICE_TIER_JSON,
  PRIORITY,
  SALESREP,
  SALESREP_JSON,
  SHIP_METHOD_JSON,
  TAX_CODE,
  TERMS,
  TERMS_JSON,
  UOM,
  UOM_JSON,
  USER,
  USER_JSON,
  WARRANTY,
  WORK_CENTER,
  WORK_CENTER_JSON,
  WORKER,
  WORKER_JSON,
  DEPARTMENT_JSON,
  ORDER_STAGE_JSON,
  PRIORITY_JSON,
  SHIP_METHOD,
  TAX_CODE_JSON,
  WARRANTY_JSON,
} from "./constants";

const useStyles = makeStyles({
  firstHeader: {
    marginTop: "10px",
  },
  subHeader: {
    fontWeight: 400,
  },
  FAB: {
    position: "fixed",
    bottom: 20,
    right: 20,
  },
  quicklinks: {
    fontSize: "smaller",
    "& a": {
      margin: "0 .2em",
    },
  },
  indent: {
    paddingLeft: "2rem",
    paddingRight: "2rem",
    "& > p:first-child": {
      textIndent: "-1em",
    },
  },
});

function References() {
  const classes = useStyles();

  return (
    <>
      <Grid container spacing={2}>
        <Grid item md={8} id="top">
          <p className={classes.quicklinks}>
            Quicklinks:
            <br />
            <HashLink to="/apidoc/references#account">Account</HashLink>
            <HashLink to="/apidoc/references#bins">Bins</HashLink>
            <HashLink to="/apidoc/references#channel">Channel</HashLink>
            <HashLink to="/apidoc/references#class">Class</HashLink>
            <HashLink to="/apidoc/references#customfield">
              Custom field
            </HashLink>
            <HashLink to="/apidoc/references#department">Department</HashLink>
            <HashLink to="/apidoc/references#location">Location</HashLink>
            <HashLink to="/apidoc/references#orderstage">Order stage</HashLink>
            <HashLink to="/apidoc/references#paymentmethod">
              Payment method
            </HashLink>
            <HashLink to="/apidoc/references#pricetier">Price tier</HashLink>
            <HashLink to="/apidoc/references#priority">Priority</HashLink>
            <HashLink to="/apidoc/references#salesrep">Sales rep</HashLink>
            <HashLink to="/apidoc/references#shipmethod">Ship method</HashLink>
            <HashLink to="/apidoc/references#taxcode">Tax code</HashLink>
            <HashLink to="/apidoc/references#terms">Terms</HashLink>
            <HashLink to="/apidoc/references#uom">Unit of measure</HashLink>
            <HashLink to="/apidoc/references#user">User</HashLink>
            <HashLink to="/apidoc/references#warranty">Warranty</HashLink>
            <HashLink to="/apidoc/references#workcenter">Work center</HashLink>
            <HashLink to="/apidoc/references#worker">Worker</HashLink>
          </p>
        </Grid>
        <Grid item md={4} />
        <Grid item md={8}>
          <h2 className={classes.firstHeader}>
            References and supporting data
          </h2>
          <p>
            Many SOS transactions refer to related records. For example, an item
            receipt transaction refers to the vendor from whom goods were
            received. This type of relationship is expressed by using a
            reference object in the transaction record. (If you are familiar
            with database concepts, this is much like a foreign key in a table
            schema. In fact, it is often the case that that is how related
            records are implemented in SOS Inventory.)
          </p>
          <p>
            A standard reference object is simply an id and a name. For example,
            if you retrieved an item receipt record, the record would have a
            vendor property that looked something like this:
          </p>
          <CodeCard>{`"vendor": { "id": 12, "name": "Vendor ABC" }`}</CodeCard>
          <p>
            When you create or update a transaction, you will often need to
            refer to a related record. For example, when creating an item
            receipt transaction, you must supply a reference to a vendor. You
            can do this in one of two ways:
          </p>
          <div className={classes.indent}>
            <p>
              1. Supply the related record id. Continuing our example, if you
              were to create a new item receipt related to Vendor ABC, you could
              specify the vendor property on the new item receipt record like
              this:
            </p>
            <CodeCard>{`"vendor": {"id": 12 }`}</CodeCard>
            <p>or</p>
            <CodeCard>
              {`"vendor": { "id": 12, "name": "Vendor ABC" }`}
              <br />
            </CodeCard>
            (The name property is optional when referencing by id.)
            <p>
              If a record with the given id does not exist, the transaction add
              or update will fail.
            </p>
          </div>
          <div className={classes.indent}>
            <p>
              2. Supply an exact match name. Using this method would look like
              this:
            </p>
            <CodeCard>{`"vendor": { "name": "Vendor ABC" }`}</CodeCard>
            (When using the name lookup method, you <em>must</em> not supply the
            id property.)
            <p>
              When using a name reference, there must exist exactly one related
              record in the database with an{" "}
              <em>exact, case-insensitive match</em> to the name supplied. In
              the example above, the reference would match "Vendor ABC" or
              "VENDOR Abc". However, if there is more than one match (such as if{" "}
              <em>both</em> "Vendor ABC" and "VENDOR Abc" existed in the vendor
              table), the transaction add or update would fail.
            </p>
          </div>

          <p>
            How do you know the ids or names of related records? Generally
            speaking, you retrieve a list of those records, which gives you both
            the id and the name of each related record. For example, you could
            retrieve vendor records and extract their ids and names.
          </p>
          <p>
            The documentation that follows lists each of the available
            configuration endpoints, along with the structure of the returned
            data from these endpoints. You may query for results in the same
            manner you query for transactions or retrieve individual records if
            needed. All the endpoints follow this pattern:
          </p>
          <CodeCard>
            Retrieve all records
            <br /> GET /api/v2/:endpoint <br />
            <br /> Retrieve a single record by id <br />
            GET /api/v2/:endpoint/:id
          </CodeCard>
          <CodeCard>
            Example: <br />
            GET /api/v2/location <br />
            <br />
            or <br /> <br />
            GET /api/v2/location/1
          </CodeCard>
        </Grid>
        <Grid item md={4} />
        <Grid item md={8}>
          <h3>Result structure</h3>
          <ApiAttribute
            name={"count"}
            description={"The number of results returned in this query."}
          />
          <ApiAttribute
            name={"totalCount"}
            description={
              "The total number of records that match the filters of this query."
            }
          />
          <ApiAttribute name={"data"} description={"An array of objects."} />
          <ApiAttribute
            name={"status"}
            description={
              "The status of the query.  Will be “ok” if successful, otherwise this matches with the message field to indicate why the call failed."
            }
          />
          <ApiAttribute
            name={"message"}
            description={
              "A descriptive message indicating why the query was unsuccessful."
            }
          />
          <h3>Arguments</h3>
          <p>
            Query parameters can be used as follows. All parameters are
            optional.
          </p>
          <CodeCard>GET /api/v2/:endpoint?start=101&maxresults=100</CodeCard>

          {QUERY_PARAMS.map((attribute) => (
            <ApiAttribute
              key={attribute.name}
              name={attribute.name}
              type={attribute.type}
              description={attribute.description}
            />
          ))}
        </Grid>
        <Grid item md={4} className="example-json">
          <Paper>
            <div className="title">
              <div className="method-example-response-title">Example JSON</div>
            </div>
            <hr />
            <div className="json-content">
              <pre>
                <code>{RESULTS}</code>
              </pre>
            </div>
          </Paper>
        </Grid>
        <Grid item md={8} id="account">
          <hr />
          <h3>Account</h3>
          <p>
            This represents an account used for posting to an accounting system.
          </p>
          <CodeCard>GET /api/v2/account</CodeCard>
          {ACCOUNT.map((attribute) => (
            <ApiAttribute
              key={attribute.name}
              name={attribute.name}
              type={attribute.type}
              description={attribute.description}
              readOnly={attribute.readOnly}
            />
          ))}
        </Grid>
        <Grid item md={4} className="example-json">
          <Paper>
            <div className="title">
              <div className="method-example-response-title">
                Account example
              </div>
            </div>
            <hr />
            <div className="json-content">
              <pre>
                <code>{ACCOUNT_JSON}</code>
              </pre>
            </div>
          </Paper>
        </Grid>
        <Grid item md={8} id="bins">
          <hr />
          <h3>Bins</h3>
          <p>
            Bins provide a way of managing the place for each item within a
            given location. Bins are available on Plus plans and Pro plans.
          </p>
          <CodeCard>GET /api/v2/bins</CodeCard>
          {BIN.map((attribute) => (
            <ApiAttribute
              key={attribute.name}
              name={attribute.name}
              type={attribute.type}
              description={attribute.description}
              readOnly={attribute.readOnly}
            />
          ))}
        </Grid>
        <Grid item md={4} className="example-json">
          <Paper>
            <div className="title">
              <div className="method-example-response-title">Bin example</div>
            </div>
            <hr />
            <div className="json-content">
              <pre>
                <code>{BIN_JSON}</code>
              </pre>
            </div>
          </Paper>
        </Grid>
        <Grid item md={8} id="channel">
          <hr />
          <h3>Channel</h3>
          <p>The sales channels configured for this account.</p>
          <CodeCard>GET /api/v2/channel</CodeCard>
          {CHANNEL.map((attribute) => (
            <ApiAttribute
              key={attribute.name}
              name={attribute.name}
              type={attribute.type}
              description={attribute.description}
              readOnly={attribute.readOnly}
            />
          ))}
        </Grid>
        <Grid item md={4} className="example-json">
          <Paper>
            <div className="title">
              <div className="method-example-response-title">
                Channel example
              </div>
            </div>
            <hr />
            <div className="json-content">
              <pre>
                <code>{CHANNEL_JSON}</code>
              </pre>
            </div>
          </Paper>
        </Grid>
        <Grid item md={8} id="class">
          <hr />
          <h3>Class</h3>
          <p>This represents the class used for class tracking.</p>
          <CodeCard>GET /api/v2/class</CodeCard>
          {CLASS.map((attribute) => (
            <ApiAttribute
              key={attribute.name}
              name={attribute.name}
              type={attribute.type}
              description={attribute.description}
              readOnly={attribute.readOnly}
            />
          ))}
        </Grid>
        <Grid item md={4} className="example-json">
          <Paper>
            <div className="title">
              <div className="method-example-response-title">Class example</div>
            </div>
            <hr />
            <div className="json-content">
              <pre>
                <code>{CLASS_JSON}</code>
              </pre>
            </div>
          </Paper>
        </Grid>
        <Grid item md={8} id="customfield">
          <hr />
          <h3>Custom field</h3>
          <p>
            Custom fields allow additional data to be captured for object
            definitions and transactions.
          </p>
          <CodeCard>GET /api/v2/customfield</CodeCard>
          {CUSTOM_FIELD.map((attribute) => (
            <ApiAttribute
              key={attribute.name}
              name={attribute.name}
              type={attribute.type}
              description={attribute.description}
              readOnly={attribute.readOnly}
            />
          ))}
        </Grid>
        <Grid item md={4} className="example-json">
          <Paper>
            <div className="title">
              <div className="method-example-response-title">
                Custom field example
              </div>
            </div>
            <hr />
            <div className="json-content">
              <pre>
                <code>{CUSTOM_FIELD_JSON}</code>
              </pre>
            </div>
          </Paper>
        </Grid>
        <Grid item md={8} id="department">
          <hr />
          <h3>Department</h3>
          <p>This represents the department used for department tracking.</p>
          <CodeCard>GET /api/v2/department</CodeCard>
          {DEPARTMENT.map((attribute) => (
            <ApiAttribute
              key={attribute.name}
              name={attribute.name}
              type={attribute.type}
              description={attribute.description}
              readOnly={attribute.readOnly}
            />
          ))}
        </Grid>
        <Grid item md={4} className="example-json">
          <Paper>
            <div className="title">
              <div className="method-example-response-title">
                Department example
              </div>
            </div>
            <hr />
            <div className="json-content">
              <pre>
                <code>{DEPARTMENT_JSON}</code>
              </pre>
            </div>
          </Paper>
        </Grid>
        <Grid item md={8} id="location">
          <hr />
          <h3>Location</h3>
          <p>
            The defined locations for the account. The Companion Plan allows
            only one location.
          </p>
          <CodeCard>GET /api/v2/location</CodeCard>
          {LOCATION.map((attribute) => (
            <ApiAttribute
              key={attribute.name}
              name={attribute.name}
              type={attribute.type}
              description={attribute.description}
              readOnly={attribute.readOnly}
            />
          ))}
        </Grid>
        <Grid item md={4} className="example-json">
          <Paper>
            <div className="title">
              <div className="method-example-response-title">
                Location example
              </div>
            </div>
            <hr />
            <div className="json-content">
              <pre>
                <code>{LOCATION_JSON}</code>
              </pre>
            </div>
          </Paper>
        </Grid>
        <Grid item md={8} id="orderstage">
          <hr />
          <h3>Order stage</h3>
          <p>The stages through which an order proceeds to completion.</p>
          <CodeCard>GET /api/v2/orderstage</CodeCard>
          {ORDER_STAGE.map((attribute) => (
            <ApiAttribute
              key={attribute.name}
              name={attribute.name}
              type={attribute.type}
              description={attribute.description}
              readOnly={attribute.readOnly}
            />
          ))}
        </Grid>
        <Grid item md={4} className="example-json">
          <Paper>
            <div className="title">
              <div className="method-example-response-title">
                Order stage example
              </div>
            </div>
            <hr />
            <div className="json-content">
              <pre>
                <code>{ORDER_STAGE_JSON}</code>
              </pre>
            </div>
          </Paper>
        </Grid>
        <Grid item md={8} id="paymentmethod">
          <hr />
          <h3>Payment method</h3>
          <p>Configured payment method types.</p>
          <CodeCard>GET /api/v2/paymentmethod</CodeCard>
          {PAYMENT_METHOD.map((attribute) => (
            <ApiAttribute
              key={attribute.name}
              name={attribute.name}
              type={attribute.type}
              description={attribute.description}
              readOnly={attribute.readOnly}
            />
          ))}
        </Grid>
        <Grid item md={4} className="example-json">
          <Paper>
            <div className="title">
              <div className="method-example-response-title">
                Payment method example
              </div>
            </div>
            <hr />
            <div className="json-content">
              <pre>
                <code>{PAYMENT_METHOD_JSON}</code>
              </pre>
            </div>
          </Paper>
        </Grid>
        <Grid item md={8} id="pricetier">
          <hr />
          <h3>Price tier</h3>
          <p>The configured price tiers. Available on Plus and Pro plans.</p>
          <CodeCard>GET /api/v2/pricetier</CodeCard>
          {PRICE_TIER.map((attribute) => (
            <ApiAttribute
              key={attribute.name}
              name={attribute.name}
              type={attribute.type}
              description={attribute.description}
              readOnly={attribute.readOnly}
            />
          ))}
        </Grid>
        <Grid item md={4} className="example-json">
          <Paper>
            <div className="title">
              <div className="method-example-response-title">
                Price tier example
              </div>
            </div>
            <hr />
            <div className="json-content">
              <pre>
                <code>{PRICE_TIER_JSON}</code>
              </pre>
            </div>
          </Paper>
        </Grid>
        <Grid item md={8} id="priority">
          <hr />
          <h3>Priority</h3>
          <p>The configured priority states.</p>
          <CodeCard>GET /api/v2/priority</CodeCard>
          {PRIORITY.map((attribute) => (
            <ApiAttribute
              key={attribute.name}
              name={attribute.name}
              type={attribute.type}
              description={attribute.description}
              readOnly={attribute.readOnly}
            />
          ))}
        </Grid>
        <Grid item md={4} className="example-json">
          <Paper>
            <div className="title">
              <div className="method-example-response-title">
                Priority example
              </div>
            </div>
            <hr />
            <div className="json-content">
              <pre>
                <code>{PRIORITY_JSON}</code>
              </pre>
            </div>
          </Paper>
        </Grid>

        <Grid item md={8} id="salesrep">
          <hr />
          <h3>Sales rep</h3>
          <p>The configured sales reps for this account.</p>
          <CodeCard>GET /api/v2/salesrep</CodeCard>
          {SALESREP.map((attribute) => (
            <ApiAttribute
              key={attribute.name}
              name={attribute.name}
              type={attribute.type}
              description={attribute.description}
              readOnly={attribute.readOnly}
            />
          ))}
        </Grid>
        <Grid item md={4} className="example-json">
          <Paper>
            <div className="title">
              <div className="method-example-response-title">
                Salesrep example
              </div>
            </div>
            <hr />
            <div className="json-content">
              <pre>
                <code>{SALESREP_JSON}</code>
              </pre>
            </div>
          </Paper>
        </Grid>

        <Grid item md={8} id="shipmethod">
          <hr />
          <h3>Ship method</h3>
          <p>The configured shipment methods for this account.</p>
          <CodeCard>GET /api/v2/shipmethod</CodeCard>
          {SHIP_METHOD.map((attribute) => (
            <ApiAttribute
              key={attribute.name}
              name={attribute.name}
              type={attribute.type}
              description={attribute.description}
              readOnly={attribute.readOnly}
            />
          ))}
        </Grid>
        <Grid item md={4} className="example-json">
          <Paper>
            <div className="title">
              <div className="method-example-response-title">
                Ship method example
              </div>
            </div>
            <hr />
            <div className="json-content">
              <pre>
                <code>{SHIP_METHOD_JSON}</code>
              </pre>
            </div>
          </Paper>
        </Grid>
        <Grid item md={8} id="taxcode">
          <hr />
          <h3>Tax code</h3>
          <p>This represents the details of a tax code.</p>
          <CodeCard>GET /api/v2/taxcode</CodeCard>
          {TAX_CODE.map((attribute) => (
            <ApiAttribute
              key={attribute.name}
              name={attribute.name}
              type={attribute.type}
              description={attribute.description}
              readOnly={attribute.readOnly}
            />
          ))}
        </Grid>
        <Grid item md={4} className="example-json">
          <Paper>
            <div className="title">
              <div className="method-example-response-title">
                Tax code example
              </div>
            </div>
            <hr />
            <div className="json-content">
              <pre>
                <code>{TAX_CODE_JSON}</code>
              </pre>
            </div>
          </Paper>
        </Grid>
        <Grid item md={8} id="terms">
          <hr />
          <h3>Terms</h3>
          <p>The configured payment terms for this account.</p>
          <CodeCard>GET /api/v2/terms</CodeCard>
          {TERMS.map((attribute) => (
            <ApiAttribute
              key={attribute.name}
              name={attribute.name}
              type={attribute.type}
              description={attribute.description}
              readOnly={attribute.readOnly}
            />
          ))}
        </Grid>
        <Grid item md={4} className="example-json">
          <Paper>
            <div className="title">
              <div className="method-example-response-title">Terms example</div>
            </div>
            <hr />
            <div className="json-content">
              <pre>
                <code>{TERMS_JSON}</code>
              </pre>
            </div>
          </Paper>
        </Grid>
        <Grid item md={8} id="uom">
          <hr />
          <h3>Unit of measure</h3>
          <p>
            The configured units of measure for this account. Available on Plus
            and Pro plans.
          </p>
          <CodeCard>GET /api/v2/uom</CodeCard>
          {UOM.map((attribute) => (
            <ApiAttribute
              key={attribute.name}
              name={attribute.name}
              type={attribute.type}
              description={attribute.description}
              readOnly={attribute.readOnly}
            />
          ))}
        </Grid>
        <Grid item md={4} className="example-json">
          <Paper>
            <div className="title">
              <div className="method-example-response-title">
                Unit of measure example
              </div>
            </div>
            <hr />
            <div className="json-content">
              <pre>
                <code>{UOM_JSON}</code>
              </pre>
            </div>
          </Paper>
        </Grid>
        <Grid item md={8} id="user">
          <hr />
          <h3>User</h3>
          <p>The users that are configured for this account.</p>
          <CodeCard>GET /api/v2/user</CodeCard>
          {USER.map((attribute) => (
            <ApiAttribute
              key={attribute.name}
              name={attribute.name}
              type={attribute.type}
              description={attribute.description}
              readOnly={attribute.readOnly}
            />
          ))}
        </Grid>
        <Grid item md={4} className="example-json">
          <Paper>
            <div className="title">
              <div className="method-example-response-title">User example</div>
            </div>
            <hr />
            <div className="json-content">
              <pre>
                <code>{USER_JSON}</code>
              </pre>
            </div>
          </Paper>
        </Grid>
        <Grid item md={8} id="warranty">
          <hr />
          <h3>Warranty</h3>
          <p>
            The configured warranty settings for this account. Supported on Plus
            and Pro plans.
          </p>
          <CodeCard>GET /api/v2/warranty</CodeCard>
          {WARRANTY.map((attribute) => (
            <ApiAttribute
              key={attribute.name}
              name={attribute.name}
              type={attribute.type}
              description={attribute.description}
              readOnly={attribute.readOnly}
            />
          ))}
        </Grid>
        <Grid item md={4} className="example-json">
          <Paper>
            <div className="title">
              <div className="method-example-response-title">
                Warranty example
              </div>
            </div>
            <hr />
            <div className="json-content">
              <pre>
                <code>{WARRANTY_JSON}</code>
              </pre>
            </div>
          </Paper>
        </Grid>
        <Grid item md={8} id="workcenter">
          <hr />
          <h3>Work center</h3>
          <p>
            The configured work centers for this account. Available on Pro Plan
            only.
          </p>
          <CodeCard>GET /api/v2/workcenter</CodeCard>
          {WORK_CENTER.map((attribute) => (
            <ApiAttribute
              key={attribute.name}
              name={attribute.name}
              type={attribute.type}
              description={attribute.description}
              readOnly={attribute.readOnly}
            />
          ))}
        </Grid>
        <Grid item md={4} className="example-json">
          <Paper>
            <div className="title">
              <div className="method-example-response-title">
                Work center example
              </div>
            </div>
            <hr />
            <div className="json-content">
              <pre>
                <code>{WORK_CENTER_JSON}</code>
              </pre>
            </div>
          </Paper>
        </Grid>
        <Grid item md={8} id="worker">
          <hr />
          <h3>Worker</h3>
          <p>
            The configured workers for this account. Available on Pro Plan only.
          </p>
          <CodeCard>GET /api/v2/worker</CodeCard>
          {WORKER.map((attribute) => (
            <ApiAttribute
              key={attribute.name}
              name={attribute.name}
              type={attribute.type}
              description={attribute.description}
              readOnly={attribute.readOnly}
            />
          ))}
        </Grid>
        <Grid item md={4} className="example-json">
          <Paper>
            <div className="title">
              <div className="method-example-response-title">
                Worker example
              </div>
            </div>
            <hr />
            <div className="json-content">
              <pre>
                <code>{WORKER_JSON}</code>
              </pre>
            </div>
          </Paper>
        </Grid>
      </Grid>
      <Fab
        className={classes.FAB}
        color="primary"
        href="/apidoc/references#top"
      >
        ^ top
      </Fab>
    </>
  );
}

export default References;
